import React from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { colors, floatingLabelMaskSx, fonts } from "../../../styles/theme";
import InfoTooltip from "../overlays/InfoTooltip";

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Zweite Zeile im aufgeklappten Menü – erklärt Werte, deren Bedeutung nicht selbsterklärend ist */
  description?: string;
  /**
   * Trennlinie über dieser Option – teilt die Liste in Segmente, z.B. Vorschläge oben, der Rest
   * darunter. Wie `dividerBefore` am einwertigen `Dropdown`. Beim Suchen bleibt die Linie
   * zwischen den Segmenten, auch wenn die markierte Option selbst herausgefiltert ist.
   */
  dividerBefore?: boolean;
}

interface MultiSelectDropdownProps {
  label: string;
  values: string[];
  options: MultiSelectOption[];
  onChange: (values: string[]) => void;
  /**
   * Beim Schließen des Menüs – der Moment, in dem der Nutzer das Feld verlässt. Für
   * Blur-Speichern: `onChange` feuert je Häkchen und würde sonst je Klick einen Request
   * auslösen (siehe docs/adr/004_autosave_dirty_check.md).
   */
  onClose?: (values: string[]) => void;
  disabled?: boolean;
  /** Text im geschlossenen Feld, solange nichts gewählt ist */
  placeholder?: string;
  /**
   * Markiert das Feld als Pflichtfeld – MUI übernimmt dafür sein eingebautes Sternchen hinter
   * dem Label, keine eigene Lösung.
   */
  required?: boolean;
  /** Fehlermeldung (z.B. für Formular-Validierung) */
  error?: boolean;
  /** Hilfetext unter dem Feld */
  helperText?: string;
  /** Erklärt das Feld bei Hover übers Label: was es ist und welche Auswirkung es hat */
  tooltip?: React.ReactNode;
  /**
   * Hintergrund hinter dem (immer hochgewanderten) Label – maskiert den Umrandungsstrich, der
   * sonst dahinter durchscheint. Wie bei `InputField`/`Dropdown` mit variant='floating'.
   */
  labelBackgroundColor?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /**
   * Macht die Liste durchsuchbar: Tippen filtert die Optionen. Wie beim einwertigen `Dropdown`
   * rendert das Feld dafür einen Autocomplete statt des Auswahlmenüs, das kein Eingabefeld kennt.
   */
  searchable?: boolean;
  /**
   * Jeder gewählte Eintrag auf einer eigenen Zeile statt aller in einer Zeile mit Komma – für
   * lange Bezeichnungen, die nebeneinander abgeschnitten würden. Das Feld wächst mit.
   */
  zeilenweise?: boolean;
  sx?: object;
}

/**
 * Mehrfachauswahl in Dropdown-Bauform: geschlossen ein Feld wie jedes andere, aufgeklappt eine
 * Liste mit Häkchen. Das Menü bleibt beim Anklicken offen, geschlossen wird bewusst.
 *
 * Bewusst dieselbe Optik wie `Dropdown` mit `variant="floating"`: ein einwertiges und ein
 * mehrwertiges Feld nebeneinander sollen sich nicht in der Bauform unterscheiden, sondern nur
 * im Verhalten. Deshalb hier keine Chips im geschlossenen Zustand, sondern die gewählten
 * Bezeichnungen als eine Zeile Text – mit `zeilenweise` eine Zeile je Eintrag.
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  values,
  options,
  onChange,
  onClose,
  disabled = false,
  placeholder = "keine Auswahl",
  required = false,
  error = false,
  helperText,
  tooltip,
  labelBackgroundColor = colors.background.paper,
  size = "small",
  fullWidth = true,
  searchable = false,
  zeilenweise = false,
  sx = {},
}) => {
  const handleChange = (event: { target: { value: unknown } }) => {
    const gewaehlt = event.target.value;
    onChange(
      typeof gewaehlt === "string"
        ? gewaehlt.split(",")
        : (gewaehlt as string[]),
    );
  };

  /** Gewählte Bezeichnungen: eine Zeile je Eintrag oder alle in einer Zeile mit Komma */
  const gewaehltAnzeige = (gewaehlteLabel: string[]): React.ReactNode =>
    zeilenweise
      ? gewaehlteLabel.map((text) => (
          <Box
            key={text}
            component="span"
            sx={{ display: "block", width: "100%" }}
          >
            {text}
          </Box>
        ))
      : gewaehlteLabel.join(", ");

  const renderValue = (gewaehlt: unknown) => {
    const ids = gewaehlt as string[];
    const gewaehlteLabel = options
      .filter((option) => ids.includes(option.value))
      .map((option) => option.label);

    if (gewaehlteLabel.length === 0) {
      return (
        <Typography
          component="span"
          sx={{
            color: colors.text.disabled,
            fontFamily: fonts.primary,
            fontSize: "inherit",
            // Typography bringt sonst eine eigene, größere Zeilenhöhe mit und macht das Feld
            // dadurch einen Pixel höher als die Nachbarfelder derselben Zeile.
            lineHeight: "inherit",
          }}
        >
          {placeholder}
        </Typography>
      );
    }
    return gewaehltAnzeige(gewaehlteLabel);
  };

  const labelNode = tooltip ? (
    <InfoTooltip title={tooltip} placement="top">
      <span>{label}</span>
    </InfoTooltip>
  ) : (
    label
  );

  const optionInhalt = (option: MultiSelectOption, checked: boolean) => (
    <>
      <Checkbox checked={checked} size="small" sx={{ mr: 1, p: 0.5 }} />
      <ListItemText
        primary={option.label}
        secondary={option.description}
        slotProps={{
          primary: { sx: { fontFamily: fonts.primary } },
          secondary: {
            sx: {
              fontFamily: fonts.primary,
              color: colors.text.secondary,
              whiteSpace: "normal",
            },
          },
        }}
      />
    </>
  );

  const trennlinie = {
    borderTop: `1px solid ${colors.border.divider}`,
    mt: 0.5,
  };

  if (searchable) {
    // Segmente aus den Trennlinien: Jede Option gehört zum Segment ihrer letzten Linie darüber.
    // Als Gruppe gerendert, damit die Linie beim Filtern zwischen den Segmenten bleibt.
    const segmentJeWert = new Map<string, string>();
    let segment = 0;
    options.forEach((option) => {
      if (option.dividerBefore) segment += 1;
      segmentJeWert.set(option.value, String(segment));
    });
    const gewaehlt = options.filter((option) => values.includes(option.value));

    return (
      <Box sx={{ width: fullWidth ? "100%" : undefined }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={options}
          value={gewaehlt}
          onChange={(_, neu) => onChange(neu.map((option) => option.value))}
          onClose={() => onClose?.(values)}
          isOptionEqualToValue={(option, wert) => option.value === wert.value}
          getOptionLabel={(option) => option.label}
          groupBy={(option) => segmentJeWert.get(option.value) ?? "0"}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          noOptionsText="Kein Treffer"
          renderValue={(werte) =>
            gewaehltAnzeige(werte.map((option) => option.label))
          }
          // `key` ist der Index der ersten Option der Gruppe in der gefilterten Liste: 0 heißt,
          // die Gruppe steht oben – dort gehört keine Linie hin.
          renderGroup={(params) => (
            <li key={params.key}>
              <Box
                component="ul"
                sx={{ p: 0, ...(params.key === 0 ? {} : trennlinie) }}
              >
                {params.children}
              </Box>
            </li>
          )}
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } =
              props as React.HTMLAttributes<HTMLLIElement> & {
                key: React.Key;
              };
            return (
              <Box component="li" key={key} {...rest}>
                {optionInhalt(option, selected)}
              </Box>
            );
          }}
          slotProps={{
            paper: { sx: { "& .MuiAutocomplete-listbox": { maxHeight: 360 } } },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={labelNode}
              error={error}
              helperText={helperText}
              required={required}
              placeholder={gewaehlt.length === 0 ? placeholder : undefined}
              slotProps={{
                ...params.slotProps,
                inputLabel: { ...params.slotProps?.inputLabel, shrink: true },
              }}
            />
          )}
          sx={{
            ...floatingLabelMaskSx(labelBackgroundColor),
            ...sx,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined }}>
      <TextField
        select
        label={labelNode}
        value={values}
        onChange={handleChange}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        error={error}
        helperText={helperText}
        required={required}
        slotProps={{
          select: {
            multiple: true,
            displayEmpty: true,
            renderValue,
            onClose: () => onClose?.(values),
            // Ohne feste Höhe wächst das Menü bei vielen Werten über den Bildschirm hinaus.
            MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } },
          },
          // Ohne das überlagert das Label den Platzhalter, solange nichts gewählt ist.
          inputLabel: { shrink: true },
        }}
        sx={{
          "& .MuiSelect-select": zeilenweise
            ? { whiteSpace: "normal" }
            : {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
          // Maskiert den Umrandungsstrich hinter dem Label (wie bei InputField).
          ...floatingLabelMaskSx(labelBackgroundColor),
          ...sx,
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={option.dividerBefore ? trennlinie : undefined}
          >
            {optionInhalt(option, values.includes(option.value))}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default MultiSelectDropdown;
